import { NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Users,
  IdCard,
  Building2,
  Briefcase,
  Package,
  Handshake,
  CreditCard,
  Wrench,
  ShieldAlert,
  ShoppingCart,
  Wallet,
  PiggyBank,
  Star,
  Mail,
  LayoutDashboard,
  CalendarRange,
  BarChart3,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { BrandMark } from "@/components/BrandMark";

const NAV_GROUPS = [
  {
    label: "Alap",
    items: [
      {
        to: "/dashboard",
        label: "Áttekintés",
        icon: LayoutDashboard,
        testId: "nav-dashboard",
        badgeKey: "urgentTotal",
      },
      { to: "/users", label: "Felhasználók", icon: Users, testId: "nav-users" },
      { to: "/companies", label: "Cégek", icon: Building2, testId: "nav-companies" },
      {
        to: "/statistics",
        label: "Statisztika",
        icon: BarChart3,
        testId: "nav-statistics",
      },
      {
        to: "/notifications",
        label: "Értesítések",
        icon: Mail,
        testId: "nav-notifications",
      },
    ],
  },
  {
    label: "HR",
    items: [
      { to: "/employees", label: "Munkavállalók", icon: IdCard, testId: "nav-employees" },
      {
        to: "/recruitment",
        label: "Toborzás",
        icon: Briefcase,
        testId: "nav-recruitment",
        badgeKey: "recruitmentNew",
      },
      {
        to: "/schedule",
        label: "Beosztás",
        icon: CalendarRange,
        testId: "nav-schedule",
      },
    ],
  },
  {
    label: "Operatív",
    items: [
      {
        to: "/partners",
        label: "Partnerek",
        icon: Handshake,
        testId: "nav-partners",
      },
      {
        to: "/subscriptions",
        label: "Előfizetések",
        icon: CreditCard,
        testId: "nav-subscriptions",
      },
      {
        to: "/maintenances",
        label: "Karbantartás",
        icon: Wrench,
        testId: "nav-maintenances",
      },
      {
        to: "/accidents",
        label: "Baleseti jegyzőkönyvek",
        icon: ShieldAlert,
        testId: "nav-accidents",
      },
    ],
  },
  {
    label: "Pénzügy",
    items: [
      {
        to: "/daily-revenues",
        label: "Napi bevételek",
        icon: Wallet,
        testId: "nav-daily-revenues",
      },
      {
        to: "/cash-registers",
        label: "Házipénztárak",
        icon: PiggyBank,
        testId: "nav-cash-registers",
      },
    ],
  },
  {
    label: "Beszerzés",
    items: [
      {
        to: "/supplier-products",
        label: "Beszállítói termékek",
        icon: Package,
        testId: "nav-supplier-products",
      },
      {
        to: "/supplier-orders",
        label: "Beszállítói rendelések",
        icon: ShoppingCart,
        testId: "nav-supplier-orders",
      },
    ],
  },
  {
    label: "Social",
    items: [
      {
        to: "/influencers",
        label: "Influenszerek",
        icon: Star,
        testId: "nav-influencers",
      },
    ],
  },
];

function useAuthedPoll(loader, intervalMs, customEvent) {
  const { user } = useAuth();
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!user) {
      setValue(0);
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    let stoppedDueToAuth = false;

    const tick = async () => {
      if (cancelled || stoppedDueToAuth) return;
      // Don't waste a request if the tab is hidden — refresh on focus instead.
      if (document.visibilityState !== "visible") return;
      try {
        const v = await loader();
        if (!cancelled) setValue(v);
      } catch (err) {
        const code = err?.response?.status;
        if (code === 401 || code === 403) {
          // Session is gone — stop polling silently.
          stoppedDueToAuth = true;
          if (timer) {
            clearInterval(timer);
            timer = null;
          }
        }
        // 502 / 503 / network: keep silent and try again on next tick.
      }
    };

    tick();
    timer = setInterval(tick, intervalMs);

    const onFocus = () => {
      if (stoppedDueToAuth) return;
      tick();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") tick();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    if (customEvent) window.addEventListener(customEvent, onFocus);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      if (customEvent) window.removeEventListener(customEvent, onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, intervalMs, customEvent]);
  return value;
}

function useRecruitmentNewCount() {
  return useAuthedPoll(
    async () => {
      const { data } = await api.get("/positions");
      return data.reduce((sum, p) => sum + (p.application_counts?.new || 0), 0);
    },
    300000,
    "recruitment:changed"
  );
}

function useUrgentTotalCount() {
  return useAuthedPoll(
    async () => {
      const { data } = await api.get("/dashboard/urgent");
      return data?.counts?.total || 0;
    },
    300000,
    "dashboard:changed"
  );
}

export function Sidebar({ collapsed, onToggle }) {
  const recruitmentNew = useRecruitmentNewCount();
  const urgentTotal = useUrgentTotalCount();
  const badges = { recruitmentNew, urgentTotal };
  return (
    <aside
      data-testid="sidebar"
      className={cn(
        "fixed inset-y-0 left-0 z-40 hidden md:flex flex-col border-r border-border bg-[hsl(var(--sipx-elevated))] transition-[width] duration-200 ease-out",
        collapsed ? "w-16" : "w-64"
      )}
    >
      {/* Brand */}
      <div
        className={cn(
          "flex h-16 items-center border-b border-border px-4",
          collapsed && "justify-center px-0"
        )}
      >
        <div className="flex items-center gap-2.5">
          <BrandMark />
          {!collapsed && (
            <div className="flex flex-col leading-tight">
              <span className="text-sm font-semibold tracking-tight">
                SIPX Group
              </span>
              <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                belső rendszer
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        <NavGroups collapsed={collapsed} badges={badges} />
      </nav>

      {/* Collapse toggle */}
      <div className="border-t border-border p-3">
        <button
          type="button"
          data-testid="sidebar-toggle"
          onClick={onToggle}
          className={cn(
            "flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-sm text-muted-foreground transition-colors hover:bg-foreground/[0.04] hover:text-foreground",
            collapsed && "justify-center px-0"
          )}
          aria-label={collapsed ? "Oldalsáv kinyitása" : "Oldalsáv összecsukása"}
        >
          {collapsed ? (
            <PanelLeftOpen className="h-4 w-4" strokeWidth={1.75} />
          ) : (
            <PanelLeftClose className="h-4 w-4" strokeWidth={1.75} />
          )}
          {!collapsed && <span>Összecsukás</span>}
        </button>
      </div>
    </aside>
  );
}

/** Shared nav body — used in both desktop Sidebar and mobile Sheet drawer. */
function NavGroups({ collapsed, badges, onNavigate }) {
  return (
    <div className="space-y-5">
      {NAV_GROUPS.map((group) => (
        <div key={group.label}>
          {!collapsed && (
            <p
              className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/80"
              data-testid={`nav-group-${group.label.toLowerCase()}`}
            >
              {group.label}
            </p>
          )}
          {collapsed && (
            <div className="mx-2 mb-1.5 h-px bg-foreground/[0.06]" aria-hidden />
          )}
          <ul className="space-y-0.5">
            {group.items.map((item) => {
              const Icon = item.icon;
              const badge = item.badgeKey ? badges[item.badgeKey] : 0;
              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    data-testid={item.testId}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cn(
                        "group relative flex items-center gap-3 rounded-md px-2.5 py-2 text-sm transition-colors",
                        isActive
                          ? "bg-foreground/[0.06] text-foreground"
                          : "text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground",
                        collapsed && "justify-center px-0"
                      )
                    }
                  >
                    <span className="relative">
                      <Icon
                        className="h-4 w-4 shrink-0"
                        strokeWidth={1.75}
                        aria-hidden
                      />
                      {collapsed && badge > 0 && (
                        <span
                          data-testid={`${item.testId}-badge-dot`}
                          className="absolute -right-1.5 -top-1.5 h-2 w-2 rounded-full bg-rose-500 ring-2 ring-[hsl(var(--sipx-elevated))]"
                        />
                      )}
                    </span>
                    {!collapsed && (
                      <span className="flex flex-1 items-center justify-between gap-2">
                        <span>{item.label}</span>
                        {badge > 0 && (
                          <span
                            data-testid={`${item.testId}-badge`}
                            className="inline-flex min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-semibold text-white"
                          >
                            {badge > 99 ? "99+" : badge}
                          </span>
                        )}
                      </span>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

/** Mobile nav drawer — rendered inside a Sheet. */
export function MobileNav({ open, onOpenChange }) {
  const recruitmentNew = useRecruitmentNewCount();
  const urgentTotal = useUrgentTotalCount();
  const badges = { recruitmentNew, urgentTotal };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        className="w-72 border-r border-border bg-[hsl(var(--sipx-elevated))] p-0"
        data-testid="mobile-nav"
      >
        <div className="flex h-16 items-center gap-2.5 border-b border-border px-4">
          <BrandMark />
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-semibold tracking-tight">
              SIPX Group
            </span>
            <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
              belső rendszer
            </span>
          </div>
        </div>
        <nav className="h-[calc(100vh-4rem)] overflow-y-auto px-3 py-4">
          <NavGroups
            collapsed={false}
            badges={badges}
            onNavigate={() => onOpenChange(false)}
          />
        </nav>
      </SheetContent>
    </Sheet>
  );
}


